/**
 * SyncProviderSelector Component
 *
 * Allows users to change their sync provider via a button and modal.
 * Designed to prevent accidental provider changes that could trigger
 * sync disconnection and data conflicts.
 */

// 1. External dependencies
import React, { useState } from 'react';
import { Cloud, HardDrive, RefreshCw } from 'lucide-react';

// 2. Internal absolute imports
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Radio,
  RadioGroup,
  Chip
} from '@enfomentis/ui';
import type { SyncProviderType } from '@/types/sync';

export interface SyncProviderSelectorProps {
  /** Currently active sync provider */
  activeSyncProvider: SyncProviderType | undefined;
  /** Callback when provider changes */
  onChange: (provider: SyncProviderType) => Promise<void>;
  /** Disable provider selection */
  disabled?: boolean;
}

/**
 * Sync Provider Selector Button
 *
 * Shows current provider with a "Change Provider" button that opens
 * a modal for selection. Prevents accidental provider switches.
 */
export const SyncProviderSelector: React.FC<SyncProviderSelectorProps> = ({
  activeSyncProvider,
  onChange,
  disabled
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedProvider, setSelectedProvider] = useState<string>(activeSyncProvider || 'local');
  const [isChanging, setIsChanging] = useState<boolean>(false);

  const handleOpenModal = (): void => {
    setSelectedProvider(activeSyncProvider || 'local');
    setIsOpen(true);
  };

  const handleClose = (): void => {
    setIsOpen(false);
    setSelectedProvider(activeSyncProvider || 'local');
  };

  const handleConfirm = async (): Promise<void> => {
    if (selectedProvider === activeSyncProvider) {
      setIsOpen(false);
      return;
    }

    // Capture provider before async operation - React re-render during await
    // would reset state, causing setIsOpen(false) after await to never execute
    const providerToChange = selectedProvider;
    setIsChanging(true);
    setIsOpen(false);  // Close modal BEFORE async to avoid re-render race condition

    try {
      await onChange(providerToChange as SyncProviderType);
    } catch (error) {
      console.error('Failed to change provider:', error);
      // Re-open modal on error so user can retry or cancel
      setIsOpen(true);
    } finally {
      setIsChanging(false);
    }
  };

  const getProviderLabel = (provider: SyncProviderType | undefined): string => {
    if (!provider) return 'None';
    switch (provider) {
      case 'local':
        return 'Local Directory';
      case 'google-drive':
        return 'Google Drive';
      case 'dropbox':
        return 'Dropbox';
      case 'onedrive':
        return 'OneDrive';
      default:
        return provider;
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex items-center gap-2">
          <Cloud className="w-5 h-5 text-text-secondary" />
          <h3 className="text-lg font-semibold">Sync Provider</h3>
        </CardHeader>
        <CardBody className="space-y-4 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-text-secondary mb-2">Current Provider</p>
              <p className="text-base font-semibold text-text-primary break-words">
                {getProviderLabel(activeSyncProvider)}
              </p>
            </div>
            <Button
              variant="bordered"
              size="lg"
              startContent={<RefreshCw className="w-4 h-4" />}
              onPress={handleOpenModal}
              isDisabled={disabled}
              className="w-full sm:w-auto"
            >
              {activeSyncProvider ? 'Change Provider' : 'Select Provider'}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Provider selection modal */}
      <Modal isOpen={isOpen} onClose={handleClose} size="md">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>
                <div className="flex items-center gap-2">
                  <Cloud className="w-5 h-5" />
                  <span>{activeSyncProvider ? 'Change Sync Provider' : 'Select Sync Provider'}</span>
                </div>
              </ModalHeader>
              <ModalBody>
                <p className="text-sm text-text-secondary mb-4">
                  Select where you want to store your synced trading journal data.
                  {activeSyncProvider && (
                    <span className="block mt-2 text-warning-DEFAULT">
                      ⚠️ Changing providers will disconnect your current sync and may require re-syncing data.
                    </span>
                  )}
                </p>

                <RadioGroup
                  value={selectedProvider}
                  onValueChange={setSelectedProvider}
                  classNames={{
                    wrapper: 'gap-3'
                  }}
                >
                  <Radio
                    value="local"
                    description="Store sync files in a local folder on your device"
                    classNames={{
                      base: 'border border-border-default rounded-lg p-3 hover:bg-surface-subtle transition-colors'
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-4 h-4" />
                      <span className="font-medium">Local Directory</span>
                    </div>
                  </Radio>

                  <Radio
                    value="google-drive"
                    description="Store sync files securely in your Google Drive"
                    classNames={{
                      base: 'border border-border-default rounded-lg p-3 hover:bg-surface-subtle transition-colors'
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Cloud className="w-4 h-4" />
                      <span className="font-medium">Google Drive</span>
                    </div>
                  </Radio>

                  <Radio
                    value="dropbox"
                    description="Coming soon"
                    isDisabled
                    classNames={{
                      base: 'border border-border-default rounded-lg p-3 opacity-50'
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Cloud className="w-4 h-4" />
                      <span className="font-medium">Dropbox</span>
                      <Chip size="sm" variant="flat" className="ml-2">Coming Soon</Chip>
                    </div>
                  </Radio>

                  <Radio
                    value="onedrive"
                    description="Coming soon"
                    isDisabled
                    classNames={{
                      base: 'border border-border-default rounded-lg p-3 opacity-50'
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Cloud className="w-4 h-4" />
                      <span className="font-medium">OneDrive</span>
                      <Chip size="sm" variant="flat" className="ml-2">Coming Soon</Chip>
                    </div>
                  </Radio>
                </RadioGroup>
              </ModalBody>
              <ModalFooter>
                <div className="flex flex-col sm:flex-row gap-2 w-full">
                  <Button
                    variant="light"
                    size="lg"
                    onPress={onClose}
                    isDisabled={isChanging}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    size="lg"
                    onPress={handleConfirm}
                    isLoading={isChanging}
                    isDisabled={selectedProvider === activeSyncProvider}
                    className="w-full sm:w-auto"
                  >
                    {selectedProvider === activeSyncProvider
                      ? 'No Change'
                      : activeSyncProvider
                        ? 'Confirm Change'
                        : 'Confirm Selection'}
                  </Button>
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
